// LoginShell — visual frame chung cho trang Login của tất cả admin (SaaS + Core).
//
// Mục đích: thống nhất gradient bg + brand header + card style + footer.
// Logic auth (form email/password, Google Sign-In) thì mỗi admin tự render qua
// children/googleSlot vì backend khác (SaaS dùng inboxClient + GSI ID token,
// Core dùng AuthContext + @react-oauth/google auth-code flow).
//
// Variants:
//   - "pink" (default): gradient pink → rose → purple — phong cách SaaS.
//   - "blue": gradient blue → indigo — phong cách Core / Enterprise neutral.
//
// Slots:
//   - children: nội dung chính trong card (form, button "Đăng nhập",...).
//   - otpSlot: đường đăng nhập CHÍNH, render TRÊN CÙNG. Chỉ Connect truyền (OTP qua SMS).
//   - googleSlot: render ngay dưới otpSlot, TRÊN gạch "hoặc". Null/undefined → ẩn.
//     ⚠️ Thứ tự từ 01/09/2026: OTP → Google → gạch "hoặc" → form mật khẩu. Trước đó form
//     mật khẩu đứng đầu còn Google bị đẩy xuống dưới gạch.
//   - footer: section dưới cùng trong card (link "Đăng ký", "Quên mật khẩu"...).
//   - belowCard: text/copy bên ngoài card phía dưới (vd "14 ngày dùng thử...").
//
// Chọn ngôn ngữ: LanguageSwitcher gắn cố định góc trên phải. Nó TỰ ẨN khi host chỉ bật
// 1 ngôn ngữ (Core khoá tiếng Việt) — host không phải truyền cờ gì. Chọn ở đây lưu ngay
// localStorage nên cả màn login lẫn app sau khi đăng nhập đều đúng ngôn ngữ, không chờ API.

import { useMemo } from 'react';
import { useLingui } from '@lingui/react/macro';
import LanguageSwitcher from '../../i18n/LanguageSwitcher.jsx';
import '../../i18n/c/pages-auth.js';

const VARIANTS = {
  pink: {
    bg: 'bg-gradient-to-br from-pink-50 via-rose-50 to-purple-50',
    blob1: 'bg-pink-200/30',
    blob2: 'bg-rose-200/30',
  },
  blue: {
    bg: 'bg-gradient-to-br from-blue-50 via-indigo-50 to-sky-50',
    blob1: 'bg-blue-200/30',
    blob2: 'bg-indigo-200/30',
  },
};

export default function LoginShell({
  logoSrc,
  logoAlt = 'CNV',
  brandName,
  tagline,
  // Mặc định để undefined rồi ?? bên dưới: giá trị mặc định ở chữ ký hàm được tính
  // TRƯỚC khi hook chạy, mà chuỗi dịch cần `t` từ useLingui().
  subtitle,
  subtitleHint,
  children,
  googleSlot,
  // Khe cho đường đăng nhập ĐỨNG ĐẦU — hiện chỉ CNV Connect dùng (mã OTP qua SMS).
  // Core + CNV Work SaaS không truyền, khe rỗng thì không render gì.
  otpSlot,
  dividerText,
  footer,
  belowCard,
  variant = 'pink',
  onLocaleChange,
}) {
  const v = useMemo(() => VARIANTS[variant] || VARIANTS.pink, [variant]);
  const { t } = useLingui();
  // Giữ NGUYÊN ngữ nghĩa cũ: không truyền → hiện chữ mặc định; truyền '' hoặc null → ẩn.
  const shownSubtitle = subtitle === undefined ? t`Đăng nhập` : subtitle;

  return (
    <div
      className={`min-h-screen ${v.bg} flex items-center justify-center p-4 relative overflow-hidden`}
    >
      {/* Decorative blur blobs */}
      <div
        className={`absolute -top-24 -left-24 w-96 h-96 ${v.blob1} rounded-full blur-3xl pointer-events-none`}
      />
      <div
        className={`absolute -bottom-24 -right-24 w-96 h-96 ${v.blob2} rounded-full blur-3xl pointer-events-none`}
      />

      <div className="w-full max-w-md relative z-10">
        {/* Brand header — logo + name + tagline */}
        <div className="text-center mb-8">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={logoAlt}
              className="w-20 h-20 mx-auto rounded-2xl shadow-lg mb-4 object-contain"
            />
          ) : null}
          {brandName ? (
            <h1 className="text-2xl font-bold text-slate-900">{brandName}</h1>
          ) : null}
          {tagline ? (
            <p className="text-xs font-semibold text-slate-500 mt-1 tracking-widest">
              {tagline}
            </p>
          ) : null}
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {shownSubtitle ? (
            <h2 className="text-xl font-semibold text-slate-900 mb-1">{shownSubtitle}</h2>
          ) : null}
          {subtitleHint ? (
            <p className="text-sm text-slate-500 mb-6">{subtitleHint}</p>
          ) : null}

          {/* ── THỨ TỰ ĐĂNG NHẬP (sếp Phú 01/09/2026) ────────────────────────────────
              *"lấy OTP làm đăng nhập chính, sau đó Google và tới mật khẩu; với các hệ khác
              thì Google làm chính sau đó là mật khẩu"*.

              Nên bố cục là: [OTP nếu có] → [Google nếu có] → gạch "hoặc" → [form mật khẩu].
              Trước 01/09 form mật khẩu đứng ĐẦU và Google bị đẩy xuống sau gạch — tức cách
              đăng nhập mà hầu hết người dùng chọn lại nằm dưới cách ít người dùng nhất.

              `otpSlot` CHỈ CNV Connect truyền (125 thợ của Sky nhớ số điện thoại chứ không
              nhớ mật khẩu). Core và CNV Work SaaS không truyền ⇒ không render gì, hai hệ đó
              chỉ đổi từ "mật khẩu trước" sang "Google trước". */}
          {otpSlot ? <div className="mb-3">{otpSlot}</div> : null}
          {googleSlot ? <div className={otpSlot ? 'mb-1' : 'mb-1'}>{googleSlot}</div> : null}

          {(otpSlot || googleSlot) ? (
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 text-slate-400">{dividerText ?? t`hoặc`}</span>
              </div>
            </div>
          ) : null}

          {children}

          {footer ? (
            <div className="mt-6 pt-6 border-t border-slate-100 text-center text-sm">
              {footer}
            </div>
          ) : null}
        </div>

        {/* Chọn ngôn ngữ — nằm TRONG cột nội dung, canh giữa dưới card (chuẩn trang
            đăng nhập Google/Odoo). Bản cũ dùng `absolute top-4 right-4` bám mép màn
            hình nên trên màn rộng nó văng hẳn ra ngoài card, trông như lỗi layout. */}
        <div className="mt-5 flex justify-center">
          <LanguageSwitcher onChange={onLocaleChange} />
        </div>

        {belowCard ? (
          <p className="text-center text-xs text-slate-400 mt-3">{belowCard}</p>
        ) : null}
      </div>
    </div>
  );
}
